// ─── Today's Primary Insight ─────────────────────────────────────────────────
//
// Hero card at the top of the dashboard. Answers:
//   - What changed?
//   - Why does it matter?
//   - Top contributors (up to 3)
//   - Suggested next step
//   - Confidence level
//
// All user-visible copy is lens-aware — the same underlying view model
// renders differently for patient vs clinician based on the current lens.
//
// This component is presentation-only. It reads a PrimaryInsight view model
// (already computed by buildDashboardVM in the transformer layer) and the
// current lens from LensContext. It does NOT derive any business logic.
// ──────────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import type {
  PrimaryInsight,
  ConfidenceLevel,
  SeverityBand,
  Lens,
} from '../../lib/dashboard/types';
import { useLens } from '../../lib/dashboard/lensContext';
import { pickLens, confidenceLabel } from '../../lib/dashboard/lensCopy';
import LensToggle from './LensToggle';

interface Props {
  /** Computed view model from buildDashboardVM. Null = no data. */
  insight: PrimaryInsight | null;
}

// ─── Visual helpers ──────────────────────────────────────────────────────────

const SEVERITY_ACCENT: Record<SeverityBand, string> = {
  excellent: '#00A896',
  good: '#22C55E',
  fair: '#F59E0B',
  attention: '#EF4444',
  no_data: '#94A3B8',
};

const CONFIDENCE_FILL: Record<ConfidenceLevel, number> = {
  high: 1.0,
  moderate: 0.66,
  low: 0.33,
  insufficient: 0.1,
};

function ConfidenceBar({ level }: { level: ConfidenceLevel }) {
  const fill = CONFIDENCE_FILL[level];
  const color =
    level === 'high'
      ? '#22C55E'
      : level === 'moderate'
      ? '#F59E0B'
      : level === 'low'
      ? '#F59E0B'
      : '#94A3B8';
  return (
    <div
      className="h-1 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(fill * 100)}
      aria-label={`Insight confidence: ${level}`}
    >
      <div
        className="h-full rounded-full transition-all"
        style={{ width: `${fill * 100}%`, backgroundColor: color }}
      />
    </div>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────────

function EmptyInsight() {
  return (
    <section
      aria-labelledby="primary-insight-empty-heading"
      className="card"
      style={{ borderLeft: '3px solid #94A3B8' }}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <h2
          id="primary-insight-empty-heading"
          className="section-label"
        >
          Today's Primary Insight
        </h2>
        <LensToggle />
      </div>
      <p className="text-lg font-semibold text-arete-navy dark:text-gray-100 mb-1">
        Connect data sources to see insights
      </p>
      <p className="text-sm text-arete-muted leading-snug">
        Once your wearable, labs, or weekly check-in data is flowing, we will
        surface the most important change in your system here.
      </p>
    </section>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function TodaysPrimaryInsight({ insight }: Props) {
  const { lens } = useLens();
  const [showRationale, setShowRationale] = useState(false);

  if (!insight) return <EmptyInsight />;

  const accent = SEVERITY_ACCENT[insight.severity];
  const headingId = `primary-insight-${insight.id}`;
  const confLabel = pickLens(confidenceLabel(insight.confidence), lens);

  return (
    // Phase 7: card override reduces padding from p-9 to p-6 so the
    // hero + System Overview + Pattern Interpretation trio fits the
    // 3-section above-fold budget on 1440×900 without hiding content.
    <section
      aria-labelledby={headingId}
      className="card !p-6 !rounded-3xl"
      style={{ borderLeft: `3px solid ${accent}` }}
      data-phase="7"
      data-testid="todays-primary-insight"
    >
      {/* Header row: eyebrow label + lens toggle */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <p className="section-label">Today's Primary Insight</p>
        <LensToggle />
      </div>

      {/* Headline */}
      <h2
        id={headingId}
        className="text-xl font-semibold text-arete-navy dark:text-gray-100 leading-tight"
      >
        {pickLens(insight.headline, lens)}
      </h2>

      {/* Explanation */}
      <p className="mt-2 text-sm text-arete-navy/80 dark:text-gray-300 leading-relaxed">
        {pickLens(insight.explanation, lens)}
      </p>

      {/* Contributors */}
      {insight.contributors.length > 0 && (
        <div className="mt-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-arete-muted mb-1.5">
            {lens === 'patient' ? 'What we looked at' : 'Contributing signals'}
          </p>
          <ul className="flex flex-col gap-1.5">
            {insight.contributors.map((c) => (
              <li
                key={c.id}
                className="flex items-baseline gap-2 text-xs text-arete-navy/90 dark:text-gray-300"
              >
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: accent }}
                  aria-hidden="true"
                />
                <span className="font-medium">{c.label}</span>
                <span className="text-arete-muted">·</span>
                <span className="text-arete-muted">{c.value}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Next step */}
      <div className="mt-4 rounded-xl bg-arete-light/60 dark:bg-white/5 px-4 py-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-arete-muted mb-0.5">
          {lens === 'patient' ? 'One thing to try' : 'Suggested next step'}
        </p>
        <p className="text-sm text-arete-navy dark:text-gray-200 leading-snug">
          {pickLens(insight.nextStep, lens)}
        </p>
      </div>

      {/* Confidence footer + rationale disclosure */}
      <div className="mt-4 flex items-center gap-3">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] font-medium text-arete-muted">
              {confLabel}
            </span>
          </div>
          <ConfidenceBar level={insight.confidence} />
        </div>
        {insight.rationale && (
          <button
            type="button"
            onClick={() => setShowRationale((v) => !v)}
            aria-expanded={showRationale}
            className="text-[11px] font-medium text-arete-blue hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-arete-blue/40 rounded px-1"
          >
            {showRationale ? 'Hide why' : 'Why this surfaced'}
          </button>
        )}
      </div>
      {insight.rationale && showRationale && (
        <p className="mt-2 text-xs text-arete-muted leading-relaxed border-l-2 border-arete-sage/40 pl-3">
          {pickLens(insight.rationale, lens)}
        </p>
      )}
    </section>
  );
}

// ─── Re-exports for tests / storybook ────────────────────────────────────────

export type { Lens };
